/**
 * iOS 26: one navigation bar through a push or pop.
 *
 * Each screen draws its own bar (the back and More circles in `.sb-navbar`,
 * the glass capsules `.sb-bar-glass` and the `leading` / `trailing` toolbar
 * items laid out on them, src/layoutDom.ts), and a screen slides as a whole,
 * so the bar's items slid with it. On iOS 26 the navigation controller draws
 * one bar: through a push or pop the items stay in it, a capsule both screens
 * have changes shape from the one's to the other's, the old screen's items
 * scale and fade out as the new screen's scale and fade in (the bar's own
 * transition for an item that comes or goes, src/animation.ts), an item both
 * screens draw the same at the same place stays as it is, and the back button
 * appears or goes with the depth. The titles still travel with their screens
 * (the large title with the content it heads), as the content does.
 *
 * The morph is an overlay on the stack (`.sb-navbar-morph`, styles.css: above
 * the screens and the pop clone, taking no input) holding visual clones of
 * both screens' bar parts, while the parts themselves are hidden
 * (`data-sb-bar-morph`) for the slide. The renderer builds it at the commit
 * that holds the push or pop, in the frame the slide starts (the layout has
 * placed the new screen's parts by then), and it drops itself when the slide
 * ends. A swipe back builds it when the drag claims the pointer and scrubs it
 * with the drag; the release plays the rest forward (the pop, from the pop
 * clone the screen leaves behind) or back (the snap back) over the slide's
 * duration, as the screens move.
 */

import { TOOLBAR_TRANSITION, transitionStart } from './animation';
import { ghostClone } from './ghost';
import type { Rect } from './layout';

/** On a screen's bar part the morph stands in for: hidden while it runs (styles.css). */
export const BAR_MORPH_ATTR = 'data-sb-bar-morph';
/** The overlay's class (styles.css). */
export const BAR_MORPH_CLASS = 'sb-navbar-morph';
/** The slide's duration under `prefers-reduced-motion` (styles.css: 0.01s). */
const REDUCED_MOTION_MS = 10;
/** The slide's easing (styles.css `--sb-nav-easing`), read from the stack; this when it cannot be. */
const NAV_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
/** Two boxes this close are one place. */
const RECT_EPSILON = 0.5;
/** A drag this close to an end has nothing left to play. */
const PROGRESS_EPSILON = 0.01;

export type BarPlacement = 'leading' | 'trailing';
export type BarPartKind = 'glass' | 'back' | 'more' | 'item';

/** A part of a screen's bar, as the plan sees it. */
export interface BarPart {
  kind: BarPartKind;
  /** The run an item or capsule belongs to; the back and More circles have none. */
  placement: BarPlacement | null;
  /** Its box, relative to the screen. */
  rect: Rect;
  /** What it draws, without element ids (two items that draw the same at the same place are one): `signatureOf`. */
  signature: string;
}

export type MorphPhase = 'keep' | 'move' | 'exit' | 'enter';

/** One clone of the morph: the part drawn (the arriving screen's, or the leaving screen's for `exit`) and how it goes from `from` to `to`. */
export interface MorphStep<T extends BarPart> {
  part: T;
  from: Rect;
  to: Rect;
  phase: MorphPhase;
}

function sameRect(a: Rect, b: Rect): boolean {
  return (
    Math.abs(a.x - b.x) < RECT_EPSILON &&
    Math.abs(a.y - b.y) < RECT_EPSILON &&
    Math.abs(a.width - b.width) < RECT_EPSILON &&
    Math.abs(a.height - b.height) < RECT_EPSILON
  );
}

/**
 * How the leaving screen's bar parts (`from`) become the arriving screen's
 * (`to`): the capsules first (drawn under the rest), each run of a placement
 * meeting the run in its place on the other screen, leading runs counted
 * from the leading edge and trailing runs from the trailing edge, where the
 * bar anchors them (a capsule with none opposite scales and fades as the
 * items do); then the back and More circles, one each; then the items, an
 * item the other screen draws the same at the same place staying as it is
 * and every other leaving or arriving.
 */
export function planBarMorph<T extends BarPart>(from: readonly T[], to: readonly T[]): MorphStep<T>[] {
  const steps: MorphStep<T>[] = [];
  const pair = (old: T | undefined, arriving: T | undefined): void => {
    if (old && arriving) {
      steps.push({ part: arriving, from: old.rect, to: arriving.rect, phase: sameRect(old.rect, arriving.rect) ? 'keep' : 'move' });
    } else if (old) {
      steps.push({ part: old, from: old.rect, to: old.rect, phase: 'exit' });
    } else if (arriving) {
      steps.push({ part: arriving, from: arriving.rect, to: arriving.rect, phase: 'enter' });
    }
  };
  for (const placement of ['leading', 'trailing'] as const) {
    const olds = from.filter((part) => part.kind === 'glass' && part.placement === placement);
    const news = to.filter((part) => part.kind === 'glass' && part.placement === placement);
    const count = Math.max(olds.length, news.length);
    for (let i = 0; i < count; i++) {
      const at = (list: readonly T[]): T | undefined => (placement === 'leading' ? list[i] : list[list.length - 1 - i]);
      pair(at(olds), at(news));
    }
  }
  for (const kind of ['back', 'more'] as const) {
    pair(
      from.find((part) => part.kind === kind),
      to.find((part) => part.kind === kind),
    );
  }
  const arriving = to.filter((part) => part.kind === 'item');
  const kept = new Set<T>();
  for (const old of from) {
    if (old.kind !== 'item') continue;
    const twin = arriving.find(
      (item) => !kept.has(item) && item.placement === old.placement && sameRect(old.rect, item.rect) && item.signature === old.signature,
    );
    if (twin) {
      kept.add(twin);
      steps.push({ part: twin, from: twin.rect, to: twin.rect, phase: 'keep' });
    } else {
      steps.push({ part: old, from: old.rect, to: old.rect, phase: 'exit' });
    }
  }
  for (const item of arriving) {
    if (!kept.has(item)) steps.push({ part: item, from: item.rect, to: item.rect, phase: 'enter' });
  }
  return steps;
}

// ---------------------------------------------------------------------------
// The screens' parts
// ---------------------------------------------------------------------------

interface ScreenPart extends BarPart {
  el: HTMLElement;
}

function placementOf(el: HTMLElement): BarPlacement | null {
  const placement = el.dataset['sbPlacement'];
  return placement === 'leading' || placement === 'trailing' ? placement : null;
}

/** A screen's (or pop clone's) glass capsules in the navigation bar, in the bar's order. */
function capsulesOf(screen: HTMLElement): HTMLElement[] {
  return Array.from(screen.querySelectorAll<HTMLElement>(':scope > .sb-bar-glass:not([data-sb-exiting])')).filter((el) => placementOf(el) !== null);
}

/** Its `leading` and `trailing` toolbar groups the bar draws. */
function groupsOf(screen: HTMLElement): HTMLElement[] {
  return Array.from(screen.querySelectorAll<HTMLElement>(':scope > .sb-navscreen-body > .sb-navscreen-content > .sb-toolbar')).filter(
    (el) => placementOf(el) !== null && !el.hidden && !el.hasAttribute('data-sb-spacer'),
  );
}

/** Its back and More circles, those shown. */
function circlesOf(screen: HTMLElement): { kind: 'back' | 'more'; el: HTMLElement }[] {
  const out: { kind: 'back' | 'more'; el: HTMLElement }[] = [];
  for (const kind of ['back', 'more'] as const) {
    const el = screen.querySelector<HTMLElement>(`:scope > .sb-navbar > .sb-navbar-${kind}`);
    if (el && !el.hidden) out.push({ kind, el });
  }
  return out;
}

/** The elements of a screen's bar the morph stands in for (hidden while it runs): the capsules, the circles and the groups with their items. */
function barElementsOf(screen: HTMLElement): HTMLElement[] {
  return [...capsulesOf(screen), ...circlesOf(screen).map((circle) => circle.el), ...groupsOf(screen)];
}

/** An element's markup as the plan compares it: without element ids, the morph's own mark, or where its toolbar put it. */
function signatureOf(el: HTMLElement): string {
  const copy = el.cloneNode(true) as HTMLElement;
  for (const node of [copy, ...copy.querySelectorAll('*')]) {
    node.removeAttribute('data-sb-id');
    node.removeAttribute('data-sb-ghost');
    node.removeAttribute(BAR_MORPH_ATTR);
  }
  copy.style.left = '';
  copy.style.top = '';
  return copy.outerHTML;
}

/** The parts of a screen's bar with their boxes relative to the screen (wherever its slide has it). */
function partsOf(screen: HTMLElement): ScreenPart[] {
  const base = screen.getBoundingClientRect();
  // The frame may be scaled to fit the page: boxes in the screen's own pixels.
  const scale = screen.offsetWidth > 0 && base.width > 0 ? base.width / screen.offsetWidth : 1;
  const rectOf = (el: HTMLElement): Rect => {
    const r = el.getBoundingClientRect();
    return { x: (r.left - base.left) / scale, y: (r.top - base.top) / scale, width: r.width / scale, height: r.height / scale };
  };
  const out: ScreenPart[] = [];
  const add = (kind: BarPartKind, placement: BarPlacement | null, el: HTMLElement, signature = ''): void => {
    const rect = rectOf(el);
    if (rect.width === 0 && rect.height === 0) return;
    out.push({ kind, placement, rect, signature, el });
  };
  for (const capsule of capsulesOf(screen)) add('glass', placementOf(capsule), capsule);
  for (const circle of circlesOf(screen)) add(circle.kind, null, circle.el);
  for (const group of groupsOf(screen)) {
    const placement = placementOf(group);
    for (const item of group.querySelectorAll<HTMLElement>(':scope > [data-sb-id], :scope > [data-sb-ghost]')) {
      if (!item.hidden) add('item', placement, item, signatureOf(item));
    }
  }
  return out;
}

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function navEasing(stack: HTMLElement): string {
  const easing = getComputedStyle(stack).getPropertyValue('--sb-nav-easing').trim();
  return easing.length > 0 ? easing : NAV_EASING;
}

/** Put a clone at `rect` in the overlay (the screen's coordinates), free of what its own layout gave it. */
function place(clone: HTMLElement, rect: Rect): void {
  const s = clone.style;
  s.position = 'absolute';
  s.left = `${rect.x}px`;
  s.top = `${rect.y}px`;
  s.width = `${rect.width}px`;
  s.height = `${rect.height}px`;
  s.margin = '0';
  s.maxWidth = 'none';
}

const px = (value: number): string => `${value}px`;

// ---------------------------------------------------------------------------
// The morph
// ---------------------------------------------------------------------------

export interface BarMorphOptions {
  /** The slide's duration in ms (reduced motion shortens it as the stylesheet does). */
  duration: number;
  /** A swipe back: built paused at its start, moved by `scrub`, then played on (`finish`) or back (`cancel`). */
  interactive: boolean;
  /** Called once the morph has dropped itself. */
  onDone?: () => void;
}

export class BarMorph {
  /** The screen (or its pop clone) whose bar leaves. */
  from: HTMLElement;
  /** The screen whose bar arrives. */
  readonly to: HTMLElement;
  private readonly overlay: HTMLElement;
  private readonly animations: globalThis.Animation[] = [];
  /** The screens' own parts, hidden until the morph is done. */
  private readonly held = new Set<HTMLElement>();
  private readonly duration: number;
  private readonly onDone: (() => void) | undefined;
  /** Paused and scrubbed by a drag (until the release plays it). */
  private interactive: boolean;
  /** Where a drag has it, 0 to 1. */
  private progress = 0;
  private disposed = false;
  private timer: number | null = null;

  /**
   * Builds the morph of the bar of `from` into the bar of `to`, both screens
   * of `stack`, from where their parts are now, and starts it (or holds it
   * for a drag); null when neither screen has a bar part.
   */
  static begin(stack: HTMLElement, from: HTMLElement, to: HTMLElement, options: BarMorphOptions): BarMorph | null {
    const steps = planBarMorph(partsOf(from), partsOf(to));
    if (steps.length === 0) return null;
    return new BarMorph(stack, from, to, steps, options);
  }

  private constructor(stack: HTMLElement, from: HTMLElement, to: HTMLElement, steps: readonly MorphStep<ScreenPart>[], options: BarMorphOptions) {
    this.from = from;
    this.to = to;
    this.onDone = options.onDone;
    this.interactive = options.interactive;
    this.duration = reducedMotion() ? REDUCED_MOTION_MS : options.duration;
    const easing = options.interactive ? 'linear' : navEasing(stack);
    const overlay = document.createElement('div');
    overlay.className = BAR_MORPH_CLASS;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.inert = true;
    // Over the arriving screen's box in the stack (its slide aside).
    place(overlay, { x: to.offsetLeft, y: to.offsetTop, width: to.offsetWidth, height: to.offsetHeight });
    for (const step of steps) {
      const clone = ghostClone(step.part.el);
      clone.removeAttribute(BAR_MORPH_ATTR);
      if (step.part.kind === 'item') clone.dataset['sbBarItem'] = step.part.placement ?? '';
      place(clone, step.to);
      const animation = this.animate(clone, step, easing);
      if (animation) this.animations.push(animation);
      overlay.append(clone);
    }
    this.overlay = overlay;
    for (const el of [...barElementsOf(from), ...barElementsOf(to)]) this.hold(el);
    stack.append(overlay);
    if (!options.interactive) this.settle();
  }

  /** The clone's tween: its box from the leaving part's to the arriving part's, and the bar's item transition in or out. */
  private animate(clone: HTMLElement, step: MorphStep<ScreenPart>, easing: string): globalThis.Animation | null {
    const start: Keyframe = {};
    const end: Keyframe = {};
    if (step.phase === 'move') {
      start['left'] = px(step.from.x);
      start['top'] = px(step.from.y);
      start['width'] = px(step.from.width);
      start['height'] = px(step.from.height);
      end['left'] = px(step.to.x);
      end['top'] = px(step.to.y);
      end['width'] = px(step.to.width);
      end['height'] = px(step.to.height);
    }
    if (step.phase === 'enter' || step.phase === 'exit') {
      const gone = transitionStart(TOOLBAR_TRANSITION, step.phase === 'exit');
      const own = clone.style.transform;
      const goneTransform = gone.transform === undefined ? own || 'none' : own ? `${own} ${gone.transform}` : gone.transform;
      const [away, here] = step.phase === 'enter' ? [start, end] : [end, start];
      away['opacity'] = gone.opacity ?? 0;
      away['transform'] = goneTransform;
      here['opacity'] = 1;
      here['transform'] = own || 'none';
    }
    if (Object.keys(start).length === 0) return null;
    const animation = clone.animate([start, end], { duration: this.duration, easing, fill: 'both' });
    if (this.interactive) {
      animation.pause();
      animation.currentTime = 0;
    }
    return animation;
  }

  private hold(el: HTMLElement): void {
    el.setAttribute(BAR_MORPH_ATTR, '');
    this.held.add(el);
  }

  /** A drag has the slide at `progress` (0: as before, 1: done): the morph follows. */
  scrub(progress: number): void {
    if (this.disposed || !this.interactive) return;
    this.progress = Math.min(1, Math.max(0, progress));
    const time = this.progress * this.duration;
    for (const animation of this.animations) animation.currentTime = time;
  }

  /**
   * The screen the drag was popping has left, `clone` sliding out in its
   * place (renderer.ts `animatePop`): the clone's bar parts hide too, and the
   * rest of the morph plays over the slide's duration, as the screens move.
   */
  popped(clone: HTMLElement): void {
    if (this.disposed) return;
    for (const el of barElementsOf(clone)) this.hold(el);
    this.from = clone;
    this.finish();
  }

  /** Plays the rest of a scrubbed morph forward over the slide's duration. */
  finish(): void {
    if (this.disposed || !this.interactive) return;
    this.play(1 - this.progress);
  }

  /** Plays a scrubbed morph back to its start over the slide's duration (the snap back), then drops it. */
  cancel(): void {
    if (this.disposed || !this.interactive) return;
    this.play(-this.progress);
  }

  /** The rest of the morph at `rate` (negative: back to the start), taking the slide's duration. */
  private play(rate: number): void {
    this.interactive = false;
    if (Math.abs(rate) <= PROGRESS_EPSILON) {
      this.dispose();
      return;
    }
    for (const animation of this.animations) {
      animation.playbackRate = rate;
      animation.play();
    }
    this.settle();
  }

  /** Drops the morph once its tweens have ended (or after the slide's duration, with none, or should they never end). */
  private settle(): void {
    const done = (): void => this.dispose();
    if (this.animations.length > 0) Promise.allSettled(this.animations.map((animation) => animation.finished)).then(done, done);
    this.timer = window.setTimeout(done, this.duration + 100);
  }

  /** Removes the overlay and shows the screens' own parts again. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    for (const animation of this.animations) animation.cancel();
    this.overlay.remove();
    for (const el of this.held) el.removeAttribute(BAR_MORPH_ATTR);
    this.held.clear();
    this.onDone?.();
  }
}
